import { useQuery } from '@apollo/client';
import { Box, Chip, List, ListItem, Paper, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'timeago.js';
import { Tooltip } from '~/components/Tooltip';
import { SeverityChip } from '~/components/announcements/SeverityChip';
import { useMarkAnnouncementsRead } from '~/components/announcements/mark-read';
import { Link, ListItemButtonLink } from '~/components/link';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { formatDate } from '~/lib/datetime';
import { DashboardAnnouncementsDocument } from '../queries/announcements.generated';

// Matching Recent Activity, which sits beside this card in the same row.
const PREVIEW_LIMIT = 10;

export const LatestAnnouncements = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const link = useLink();
  const { announcementCount } = useAppContext();
  const [markRead] = useMarkAnnouncementsRead();

  const { data } = useQuery(DashboardAnnouncementsDocument, {
    skip: announcementCount === 0,
    variables: { page: 1, perPage: PREVIEW_LIMIT },
  });

  const announcements = data?.announcements.nodes ?? [];

  if (announcements.length === 0) return null;

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '5px solid',
        borderColor: theme.palette.primary.main,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography component="h2" variant="h5">
          {t('pages.dashboard.announcements.title')}
        </Typography>
        <Link to={link('/announcements')} variant="body1">
          {t('pages.dashboard.view_all')}
        </Link>
      </Box>
      <List disablePadding>
        {announcements.map((announcement) => (
          <ListItem
            key={announcement.id}
            disablePadding
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              '&:last-child': {
                borderBottom: 'none',
              },
            }}
          >
            <ListItemButtonLink
              to={link(`/announcements/${announcement.id}`)}
              onClick={() => {
                if (!announcement.read) {
                  void markRead({
                    variables: { input: { announcementIds: [announcement.id], read: true } },
                  });
                }
              }}
              disableGutters
              sx={{
                py: 1.5,
                '&:hover': {
                  backgroundColor: theme.palette.grey[50],
                },
              }}
            >
              <Typography
                variant="body1"
                sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', pr: 2 }}
              >
                {announcement.title}
              </Typography>
              {/* Before the severity chip, which every row has — the conditional one going
                  first is what keeps the severity chips in line down the list. */}
              {!announcement.read && (
                <Chip
                  label={t('components.announcements.unread')}
                  size="small"
                  color="success"
                  sx={{ mr: 1 }}
                />
              )}
              <SeverityChip severity={announcement.severity} sx={{ mr: 2 }} />
              <Tooltip title={formatDate(announcement.publishedAt)}>
                <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {format(announcement.publishedAt, i18n.language)}
                </Typography>
              </Tooltip>
            </ListItemButtonLink>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};
